import axios, { type AxiosInstance, type AxiosError } from 'axios';

/**
 * Axios instance untuk semua panggilan ke Laravel backend.
 *
 * - `withCredentials: true` supaya cookie session Laravel ikut kirim di tiap request.
 * - XSRF-TOKEN cookie auto di-baca axios dan di-kirim sebagai header X-XSRF-TOKEN
 *   (default `xsrfCookieName: 'XSRF-TOKEN'`, `xsrfHeaderName: 'X-XSRF-TOKEN'`).
 * - baseURL kosong — karena dev pakai Vite proxy + prod same-origin.
 */
export const api: AxiosInstance = axios.create({
    baseURL: '',
    withCredentials: true,
    headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

// Interceptor 401: redirect ke login HANYA kalau user sedang di protected route
// (bukan di /app/login, /app/activate, atau saat probing /api/auth/me).
// Kalau tidak dibatasi, akan loop: me() gagal → redirect → me() gagal lagi.
//
// Catatan: pakai React Router lebih clean dari window.location, tapi interceptor
// tidak punya akses ke navigate — jadi hard redirect untuk case session expired
// mid-session di protected route.
const PUBLIC_APP_PATHS = ['/app/login', '/app/activate'];
const SKIP_REDIRECT_URLS = ['/api/auth/me', '/api/auth/login', '/api/auth/activate'];

api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {
            const reqUrl = error.config?.url ?? '';
            const onPublicAppPath = PUBLIC_APP_PATHS.some((p) =>
                window.location.pathname.startsWith(p)
            );
            const isAuthProbe = SKIP_REDIRECT_URLS.some((u) => reqUrl.includes(u));
            const onProtectedPath =
                window.location.pathname.startsWith('/app/') && !onPublicAppPath;

            if (onProtectedPath && !isAuthProbe) {
                window.location.assign('/app/login');
            }
        }
        return Promise.reject(error);
    }
);

export type ApiEnvelope<T = unknown> = {
    status: 'ok' | 'error';
    message?: string;
    data?: T;
};

/**
 * Helper: extract pesan error meaningful dari AxiosError (atau error biasa).
 * Priority:
 *   1. response.data.message       (Laravel: controller custom atau validation)
 *   2. response.data.errors        (Laravel FormRequest — flatten ke string)
 *   3. response.data.resultDesc    (Peruri direct response)
 *   4. error.message               (default axios "Request failed with status code X")
 *   5. fallback string
 */
export function extractErrorMessage(err: unknown, fallback = 'Terjadi kesalahan'): string {
    if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const data = err.response?.data as {
            message?: string;
            errors?: Record<string, string[]>;
            resultDesc?: string;
        } | undefined;

        if (data?.message) {
            // Kalau ada errors[] juga (validation), ambil first error sebagai primary
            if (data.errors) {
                const firstField = Object.values(data.errors)[0];
                if (firstField?.[0]) return firstField[0];
            }
            return data.message;
        }

        if (data?.errors) {
            const allErrors: string[] = [];
            Object.values(data.errors).forEach((msgs) => {
                if (Array.isArray(msgs)) allErrors.push(...msgs);
            });
            if (allErrors.length) return allErrors.join(' · ');
        }

        if (data?.resultDesc) return data.resultDesc;

        // Generic HTTP status hint
        if (status) {
            return `HTTP ${status}: ${err.message}`;
        }
    }

    if (err instanceof Error) return err.message;
    return fallback;
}

/**
 * Wrapper untuk axios call yang auto-parse error jadi Error dengan pesan jelas.
 * Usage: `const data = await callApi(() => api.post('/api/...', body));`
 */
export async function callApi<T>(fn: () => Promise<{ data: T }>): Promise<T> {
    try {
        const res = await fn();
        return res.data;
    } catch (e) {
        throw new Error(extractErrorMessage(e));
    }
}
